import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import label from './i18n/label'
import Notify from './components/shared/Notify'

/** Translate in JavaScript: const t = useT(); t('save'). */
export const useT = () => {
  const intl = useIntl()
  return useCallback((key, values) => label(intl, key, values), [intl])
}

const EMPTY = { loading: false, error: null, value: null }

/**
 * Read a server-state resource from the store and ask for it on mount / when the key changes.
 * `load` is an action creator thunk (see store/dataActionCreators.js).
 */
export function useResource(key, load) {
  const dispatch = useDispatch()
  const entry = useSelector((s) => s.data[key]) || EMPTY
  const reload = useCallback(() => dispatch(load()), [dispatch, load])
  useEffect(() => {
    reload()
  }, [key]) // eslint-disable-line react-hooks/exhaustive-deps
  return { data: entry.value, loading: entry.loading, error: entry.error, reload }
}

/** Human text for an API failure, including per-field and unmet-requirement lists. */
export function describeError(t, err) {
  if (!err) return t('error_unexpected')
  const parts = [err.error || t('error_unexpected')]
  const f = err.fields
  if (f) {
    for (const v of Object.values(f)) {
      if (Array.isArray(v)) parts.push(...v)
      else if (typeof v === 'string' && !parts.includes(v)) parts.push(v)
    }
  }
  return parts.join(' ')
}

/** Run an API call with a busy flag, a success toast and a readable error toast. */
export function useAction() {
  const t = useT()
  const [busy, setBusy] = useState(false)
  const run = useCallback(
    async (fn, { success, onDone } = {}) => {
      setBusy(true)
      try {
        const result = await fn()
        if (success) Notify.success(t(success))
        if (onDone) await onDone(result)
        return result
      } catch (err) {
        Notify.error(describeError(t, err))
        return undefined
      } finally {
        setBusy(false)
      }
    },
    [t],
  )
  return { busy, run }
}

export const useAuthUser = () => useSelector((s) => s.auth.user)
export const useCan = (perm) => useSelector((s) => !!s.auth.user && s.auth.user.permissions.includes(perm))
